import { useEffect, useMemo, useState } from 'react'
import type { TenantBootstrapModel } from '@unified-lk/tenant-config'
import type { AuthSession } from '../../types/plans'
import { Button } from '@unified-lk/ui'
import type { StaffScheduleData } from '../../types/plans'
import { StaffScheduleAppointmentsList } from './schedule/StaffScheduleAppointmentsList'
import { StaffScheduleTimeGrid } from './schedule/StaffScheduleTimeGrid'

type ViewMode = 'grid' | 'list'

function pickDefaultDate(data: StaffScheduleData): string {
  const today = new Date().toISOString().slice(0, 10)
  if (data.days.some((d) => d.date === today)) return today

  const firstWithRecords = data.days.find((d) => d.appointments > 0)
  if (firstWithRecords) return firstWithRecords.date

  return data.days[0]?.date ?? today
}

export function StaffScheduleSection({
  bootstrap,
  session,
  data,
  loading,
  error,
  onRetry,
  canIssueSecretLinks = false,
  autoOpenSecretLinks = false,
  onIssuePatientSecretLink,
}: {
  bootstrap: TenantBootstrapModel
  session: AuthSession
  data: StaffScheduleData | null
  loading: boolean
  error: string | null
  onRetry: () => void
  canIssueSecretLinks?: boolean
  autoOpenSecretLinks?: boolean
  onIssuePatientSecretLink?: (patientId: number) => Promise<string>
}) {
  const [view, setView] = useState<ViewMode>('grid')
  const [selectedDate, setSelectedDate] = useState<string>('')

  const defaultDate = useMemo(() => (data ? pickDefaultDate(data) : ''), [data])

  useEffect(() => {
    if (!data) return
    if (!selectedDate || !data.days.some((d) => d.date === selectedDate)) {
      setSelectedDate(defaultDate)
    }
  }, [data, defaultDate, selectedDate])

  if (loading) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm leading-6 text-slate-500 shadow-sm">
        <p>Загружаем расписание...</p>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="grid gap-3 rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <p className="text-sm leading-6 text-slate-500">{error ?? 'Расписание пока недоступно.'}</p>
        <Button variant="secondary" type="button" onClick={onRetry}>
          Повторить
        </Button>
      </div>
    )
  }

  return (
    <div className="grid gap-4">
      <section className="grid gap-3 rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="mb-1 text-xs font-bold uppercase tracking-[0.14em] text-slate-500">Schedule</p>
            <h2 className="text-xl font-semibold text-slate-900">Расписание</h2>
            <p className="mt-1 text-sm leading-6 text-slate-500">
              Сетка времени и врачей. Кликните на визит, чтобы посмотреть детали.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex overflow-hidden rounded-md border border-slate-300 text-xs font-medium">
              <button
                type="button"
                onClick={() => setView('grid')}
                className={`px-3 py-1.5 transition-colors ${
                  view === 'grid'
                    ? 'bg-slate-800 text-white'
                    : 'bg-white text-slate-600 hover:bg-slate-100'
                }`}
              >
                Сетка
              </button>
              <button
                type="button"
                onClick={() => setView('list')}
                className={`border-l border-slate-300 px-3 py-1.5 transition-colors ${
                  view === 'list'
                    ? 'bg-slate-800 text-white'
                    : 'bg-white text-slate-600 hover:bg-slate-100'
                }`}
              >
                Список
              </button>
            </div>
            <Button variant="secondary" type="button" onClick={onRetry}>
              Обновить
            </Button>
          </div>
        </div>
      </section>

      {view === 'grid' ? (
        <StaffScheduleTimeGrid
          bootstrap={bootstrap}
          session={session}
          data={data}
          selectedDate={selectedDate || defaultDate}
          onSelectDate={setSelectedDate}
          canIssueSecretLinks={canIssueSecretLinks}
          autoOpenSecretLinks={autoOpenSecretLinks}
          onIssuePatientSecretLink={onIssuePatientSecretLink}
        />
      ) : (
        <StaffScheduleAppointmentsList
          appointments={data.appointments}
          canIssueSecretLinks={canIssueSecretLinks}
          autoOpenSecretLinks={autoOpenSecretLinks}
          onIssuePatientSecretLink={onIssuePatientSecretLink}
        />
      )}
    </div>
  )
}
